export const getTradeCardSelectionType = (
   tradeData: ITradeData,
   cell: ICell,
   playerOrder: number
): TradeCardSelectionType => {
   const isGivenProperty = tradeData.activeTrade?.givenProperties.includes(
      cell.order
   );
   const isTakenProperty = tradeData.activeTrade?.takenProperties.includes(
      cell.order
   );
   const isOfferingPlayer =
      tradeData.activeTrade?.offeringPlayerOrder === playerOrder;
   const isOfferedPlayer =
      tradeData.activeTrade?.offeredPlayerOrder === playerOrder;
   if (isGivenProperty && isOfferingPlayer) return "give";
   if (isGivenProperty && isOfferedPlayer) return "take";
   if (isTakenProperty && isOfferingPlayer) return "take";
   if (isTakenProperty && isOfferedPlayer) return "give";
   return "normal";
};

// Which draft states let the offering player still pick properties — on the board
// and on the small cards alike. "updating-offer" is the re-edit of an already
// offered trade and "negotiating" is the offered player reworking it into a
// counter-offer, so both are just as editable as a fresh draft.
export const isTradeDraftEditable = (status: TradeDraftStatus): boolean =>
   status === "createing-offer" ||
   status === "declined" ||
   status === "updating-offer" ||
   status === "negotiating";

// Is the trade drawer actually on screen? It only renders once its draft
// resolves to two real players (see CreateTradeModal's early return), so
// anything that mirrors the drawer — the board dimming, the give/take rings —
// has to key off the same thing. Keying off modalData.trade.visible alone is
// what used to leave the whole board dimmed into trade mode after the drawer
// had stopped rendering, with no drawer left to close it from.
export const isTradeUiActive = (state: IGlobalState): boolean => {
   if (!state.modalData.trade?.visible && !state.viewedTradeId) return false;
   const trade = state.tradeData.activeTrade;
   if (!trade) return false;
   const players = state.myGame?.situation?.players || [];
   return (
      players.some((p) => p.order === trade.offeringPlayerOrder) &&
      players.some((p) => p.order === trade.offeredPlayerOrder)
   );
};

// Label, icon and colour for the "this isn't a first offer" badge, shared by the
// trade drawer's header and the trades list row so the two can never drift into
// describing the same offer differently. Sizing is left to the caller: the two
// places it renders sit at very different scales.
export const tradeOriginBadge = (
   origin: TradeOrigin
): { label: string; icon: string; className: string } =>
   // Quiet tinted chips, same recipe as the panel's other small labels (faint
   // border, faint wash, light text) — they are a note about the offer, not a
   // second call to action competing with the row itself. The colours are the
   // two hues the game doesn't already spend, so they still read as distinct
   // without shouting: amber is the trade panel itself, emerald is money and
   // completed trades, red is decline and the expiring countdown.
   origin === "updated"
      ? {
           label: "Updated",
           icon: "fa-solid fa-pen-to-square",
           className: "border-cyan-300/40 bg-cyan-400/20 text-cyan-200",
        }
      : {
           label: "Negotiated",
           icon: "fa-solid fa-reply",
           className: "border-fuchsia-300/40 bg-fuchsia-400/20 text-fuchsia-200",
        };

// Is this draft an actual deal, or a gift? A trade has to be two-way: each side
// must put something in, and at least one property has to change hands.
//
// The cases this exists to stop, both of which the compose UI otherwise lets you
// build and send:
//   - cash-only offers (money moves, nothing else does), and
//   - offers where one player hands over properties *and* cash and gets nothing
//     back — the cash direction is a single slider, so it is easy to point it the
//     wrong way and turn an offer into a donation without noticing.
//
// Returns the message to show, or null when the draft is fine. Worded from the
// composer's point of view: they are always the offering player, including when
// negotiating (the draft is flipped into their perspective on entry).
export const getTradeOneSidedError = (
   trade: ITrade | undefined
): string | null => {
   if (!trade) return null;
   const givenCount = trade.givenProperties?.length || 0;
   const takenCount = trade.takenProperties?.length || 0;
   const moneyGiven = trade.moneyGiven || 0;
   const moneyTaken = trade.moneyTaken || 0;

   if (!givenCount && !takenCount && !moneyGiven && !moneyTaken)
      return "There is nothing in this trade yet — pick something from each side.";

   // Cash-only. Checked before the two-way rule so a money-for-money draft (which
   // the sliders don't produce, but a restored draft could carry) is named for
   // what it is rather than reported as one-sided.
   if (!givenCount && !takenCount)
      return "A trade can't be cash only — include at least one property.";

   const offeringGivesSomething = givenCount > 0 || moneyGiven > 0;
   const offeredGivesSomething = takenCount > 0 || moneyTaken > 0;
   if (!offeringGivesSomething)
      return "One-sided trades aren't allowed — offer a property or some cash of your own.";
   if (!offeredGivesSomething)
      return "One-sided trades aren't allowed — ask for a property or some cash in return.";

   return null;
};

// Can this trade still be put on the table as-is? Mirrors the server's
// isTradeStillValid (both players around, still own what they're offering, can
// afford the cash) and adds the one rule that only exists in the UI: a property
// with houses on it isn't tradable, so a draft holding one can't be restored.
// Used to decide whether a previously declined offer is worth re-opening as a
// draft, rather than starting the player from scratch.
export const isTradeDraftStillValid = (
   game: IGameDetails | undefined,
   trade: ITrade | undefined
): boolean => {
   if (!game?.situation || !trade) return false;
   const players = game.situation.players || [];
   const offerer = players.find((p) => p.order === trade.offeringPlayerOrder);
   const offered = players.find((p) => p.order === trade.offeredPlayerOrder);
   if (!offerer || !offered) return false;
   if (offerer.isBankrupt || offered.isBankrupt) return false;

   const housesData = game.situation.housesData || [];
   const hasHouses = (propertyOrder: number) =>
      !!(housesData.find((h) => h.propertyOrder === propertyOrder)?.houses || 0);

   const given = trade.givenProperties || [];
   const taken = trade.takenProperties || [];
   if (!given.every((order) => offerer.properties.includes(order))) return false;
   if (!taken.every((order) => offered.properties.includes(order))) return false;
   if ([...given, ...taken].some(hasHouses)) return false;

   // Only enforce solvency on the side actually paying, matching the server.
   if (trade.moneyGiven > 0 && offerer.money < trade.moneyGiven) return false;
   if (trade.moneyTaken > 0 && offered.money < trade.moneyTaken) return false;

   // An empty trade is technically valid but there is nothing to restore.
   return !!(given.length || taken.length || trade.moneyGiven || trade.moneyTaken);
};
